/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import { isNullOrUndefined } from "/Assets/js/utilities.js";
import Recaptcha from "./cloudflare-recaptcha.js";
import intlTelInput from "/Assets/libs/intl-tel-input/js/intlTelInput.js";

export default class FormValidation {
    constructor(embeddedFormBlock) {
        this.embeddedFormBlock = embeddedFormBlock;
        this.recaptcha = new Recaptcha();
    }

    initialize() {
        if (this.embeddedFormBlock.isHidden || !this.embeddedFormBlock.form)
            return;

        // Update the JS enabled state.
        document.body.dataset.jsEnabled = "true";

        if (window.turnstile != undefined) {
            this.recaptcha.init();
        }

        // Update D365 regex for email
        this.updateEmailRegex();
        this.bindEvents();
        this.initMultiSelectCheckboxes();
    }

    bindEvents() {
        const phoneFields = new Map([...this.embeddedFormBlock.embeddedFormFields].filter(([key, value]) => key === "phone"));
        const hasPhoneField = phoneFields.size > 0;

        if (this.embeddedFormBlock.form == null)
            return;

        const inputElements = this.embeddedFormBlock.form.elements;
        if (!inputElements)
            return;

        for (const inputEl of inputElements) {
            const isMultiSelect = inputEl.closest("div.multiselect") != null;

            if (!isMultiSelect) {
                if (inputEl.type === "radio" || inputEl.type === "checkbox") {
                    inputEl.addEventListener("change", (event) =>
                        this.updateValidationStateForInput(event.target)
                    );
                } else {
                    let isPhoneField = false;
                    if (hasPhoneField) {
                        for (let [key, value] of phoneFields.entries()) {
                            if (value === inputEl.id) {
                                isPhoneField = true;
                            }
                        }
                    }
                    if (isPhoneField) {
                        inputEl.addEventListener("change", (event) =>
                            this.validatePhoneNumberField(event.target)
                        );
                        inputEl.addEventListener("blur", (event) =>
                            this.validatePhoneNumberField(event.target)
                        );

                    } else {
                        inputEl.addEventListener("input", (event) =>
                            this.updateValidationStateForInput(event.target)
                        );
                        inputEl.addEventListener("blur", (event) =>
                            this.updateValidationStateForInput(event.target)
                        );
                        inputEl.addEventListener("change", (event) =>
                            this.updateValidationStateForInput(event.target)
                        );
                        // Adding `aria-invalid` provides users who use assistive technology
                        // with a way to know if the input is invalid.
                        // Should initially be set to `false` before interaction/validity check.
                        inputEl.setAttribute("aria-invalid", "false");

                        // Update the state for prefilled inputs.
                        if (inputEl.value !== "") {
                            this.updateValidationStateForInput(inputEl);
                        }
                    }
                }
            }
        }

        // Use form button `click` event to validate with Constraint Validation API instead.
        // D365 API already has form submit event so we need to validate before form submit fires
        const submitButton = this.embeddedFormBlock.form.querySelector("button.lp-form-button");
        submitButton.addEventListener("click", (event) => {
            event.preventDefault();
            this.onSubmit();
        });
    }

    updateEmailRegex() {
        const emailRegex = "^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$";
        const emailCntnr = this.embeddedFormBlock.embeddedFormBlockCntnr.querySelector("[data-editorblocktype=Field-email]");
        if (emailCntnr) {
            emailCntnr.querySelector("input").setAttribute("pattern", emailRegex);
        }
    }

    getValidationMessageForInput(inputEl) {
        const parent = inputEl.closest("[data-editorblocktype]");
        const isMultiSelect = inputEl.closest("div.multiselect") != null;

        // If the input is valid, return an empty string.
        if (!isMultiSelect && inputEl.validity.valid) return "";

        /**
         * Validation constraints:
         * - required
         * - type=email
         */
        if (inputEl.type === "email") {
            if (inputEl.validity.valueMissing) {
                return inputEl.validationMessage;
            }
            if (inputEl.validity.patternMismatch || inputEl.validity.typeMismatch) {
                return window.jsl10n.JsResources.Translations.Common.EnterValidEmailAddress;
            }
        }

        if (isMultiSelect) {
            return window.jsl10n.JsResources.Translations.Common.SelectAtLeastOneOption;
        }

        // If all else fails, return the default message.
        return window.jsl10n.JsResources.Translations.Common.ThisFieldRequired;
    }

    validatePhoneNumberField(inputEl) {
        const id = inputEl.id;

        const phoneInput = document.getElementById(id);
        if (!phoneInput)
            return;

        let validationMessage = "";
        let isValid = phoneInput.checkValidity();

        if (isValid) {
            const iti = intlTelInput.getInstance(phoneInput);
            const isValidNumber = iti.isValidNumber();

            if (isValidNumber) {
                const fullNumber = iti.getNumber();
                phoneInput.value = fullNumber;
                isValid = true;
                validationMessage = "";
            } else {
                isValid = false;
                validationMessage = phoneInput.dataset.validPhoneErrorMessage;
            }
        } else {
            validationMessage = phoneInput.validationMessage;
        }

        if (!phoneInput.required && phoneInput.value == "" && isValid) {
            phoneInput.classList.remove("is-valid", "is-invalid");
        } else {
            phoneInput.classList.toggle("is-valid", isValid);
            phoneInput.classList.toggle("is-invalid", !isValid);
        }
        phoneInput.setAttribute("aria-invalid", String(!isValid));

        let errorEl;
        let prevErrorEl = phoneInput.closest(".lp-form-field").querySelector(".Form__Element__ValidationError");
        if (prevErrorEl !== null) {
            errorEl = prevErrorEl;
        } else {
            errorEl = document.createElement("span");
        }
        errorEl.className = "Form__Element__ValidationError";
        errorEl.textContent = validationMessage;
        phoneInput.parentElement.after(errorEl);

        errorEl.hidden = isValid;

        return isValid;
    }

    validateAllPhoneNumberFields() {
        // eslint-disable-next-line no-undef
        const phoneFields = new Map([...this.embeddedFormBlock.embeddedFormFields].filter(([key, value]) => key === "phone"));
        const hasPhoneField = phoneFields.size > 0;
        let isValid = true;
        if (!hasPhoneField)
            return isValid;

        for (let [key, value] of phoneFields.entries()) {
            const phoneInput = document.getElementById(value);

            if (!phoneInput)
                return isValid;

            isValid = this.validatePhoneNumberField(phoneInput);
        }
        return isValid;
    }

    initMultiSelectCheckboxes() {
        const multiSelectFields = this.embeddedFormBlock.form.querySelectorAll("div.multiselect");
        const hasMultiSelectField = multiSelectFields.length > 0;

        if (!hasMultiSelectField)
            return;

        multiSelectFields.forEach(item => {
            const parent = item.closest("[data-editorblocktype]");
            const select = parent.querySelector("select");
            const itemId = select.id;
            const multiSelectCheckboxInputEls = parent.querySelectorAll("input.lp-form-fieldInput");

            for (const checkboxInputEl of multiSelectCheckboxInputEls) {
                checkboxInputEl.addEventListener("change", () =>
                    this.validateMultiSelectCheckboxGroup(item)
                );
                checkboxInputEl.addEventListener("blur", (event) => {
                    const activeEl = event.relatedTarget;
                    if (activeEl?.getAttribute("id") !== itemId) {
                        this.validateMultiSelectCheckboxGroup(item);
                    }
                });
            }

            const isMultiSelectGroupChecked = parent.querySelectorAll("input.lp-form-fieldInput:checked").length > 0;
            if (isMultiSelectGroupChecked) {
                this.validateMultiSelectCheckboxGroup(item);
            }
        });
    }

    updateValidationStateForInput(inputEl) {
        // Check if the input is valid using the Constraint Validation API.
        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/checkValidity

        const isRecaptchaInput = inputEl.getAttribute("name") == "cf-turnstile-response";
        if (isRecaptchaInput)
            return;

        const inputParent = inputEl.closest("[data-editorblocktype]");
        const isHiddenInput = inputParent.hasAttribute("hidden") && inputParent.getAttribute("hidden") == "hidden";
        if (isHiddenInput) // ignore hidden fields
            return;

        const isInputValid = inputEl.checkValidity();

        // Handle optional empty fields
        // For Firefox and Safari: Also check if valid because type="number" fields
        // report an empty string value when they have a non-numeric values.
        if (!inputEl.required && inputEl.value === "" && isInputValid) {
            // Clear validation state
            inputEl.classList.remove("is-valid", "is-invalid");
        } else {
            // Required fields: Toggle valid/invalid state classes
            inputEl.classList.toggle("is-valid", isInputValid);
            inputEl.classList.toggle("is-invalid", !isInputValid);
        }
        // Update the `aria-invalid` state when validation occurs
        // https://www.w3.org/WAI/WCAG21/Techniques/aria/ARIA21
        inputEl.setAttribute("aria-invalid", String(!isInputValid));

        // Get validation message
        //   https://developer.mozilla.org/en-US/docs/Web/API/HTMLObjectElement/validationMessage
        const validationMessage = this.getValidationMessageForInput(inputEl);

        //check form element type for placement of error message
        const isAfterParent = inputEl.type == "radio" || inputEl.type == "checkbox" ? true : false;

        // When JS is enabled, the default built-in error messages are not shown, the code needs to set the error messages manually.
        let prevErrorEl = inputParent.querySelector(".Form__Element__ValidationError");

        let errorEl;
        if (prevErrorEl !== null) {
            errorEl = prevErrorEl;
        } else {
            errorEl = document.createElement("span");
        }
        errorEl.className = "Form__Element__ValidationError";
        errorEl.textContent = validationMessage;

        if (inputEl.type === "radio" || inputEl.type === "checkbox") {
            errorEl.className = "Form__Element__ValidationError error-icon";
        }

        if (isAfterParent) {
            const parentMarketingContainer = inputParent.querySelector(".marketing-field");
            parentMarketingContainer.append(errorEl);
        } else {
            inputEl.after(errorEl);
        }
        // Show/hide the error message depending on the input's validity.
        errorEl.hidden = isInputValid;
    }

    validateMultiSelectCheckboxGroup(inputEl) {
        const parent = inputEl.closest("[data-editorblocktype]");
        const isVisible = parent.style.display != "none";

        if (!isVisible)
            return;

        const multiSelectCheckboxInputEls = parent.querySelectorAll("input.lp-form-fieldInput");
        const select = parent.querySelector("select");
        const selectId = select.id;

        const formData = new FormData(this.embeddedFormBlock.form);
        const isValid = formData.getAll(selectId).length > 0;

        select.classList.toggle("is-valid", isValid);
        select.classList.toggle("is-invalid", !isValid);
        select.setAttribute("aria-invalid", String(!isValid));

        // Update the validation UI state for each checkbox.
        for (const checkboxInputEl of multiSelectCheckboxInputEls) {
            checkboxInputEl.classList.toggle("is-valid", isValid);
            checkboxInputEl.classList.toggle("is-invalid", !isValid);
        }

        // Get validation message
        const validationMessage = this.getValidationMessageForInput(inputEl);
        const prevErrorEl = parent.querySelector(".Form__Element__ValidationError");

        let errorEl;
        if (prevErrorEl !== null) {
            errorEl = prevErrorEl;
        } else {
            errorEl = document.createElement("span");
        }
        errorEl.className = "Form__Element__ValidationError";
        errorEl.textContent = validationMessage;

        const parentMarketingContainer = parent.querySelector(".marketing-field");
        parentMarketingContainer.append(errorEl);

        // Show/hide the error message depending on the input's validity.
        errorEl.hidden = isValid;

        return isValid;
    }

    validateMultiSelectCheckboxGroups() {
        const multiSelectFields = this.embeddedFormBlock.form.querySelectorAll("div.multiselect");
        const hasMultiSelectField = multiSelectFields.length > 0;

        if (!hasMultiSelectField)
            return true;

        let isValid = true;
        multiSelectFields.forEach(item => {
            const isFieldValid = this.validateMultiSelectCheckboxGroup(item);
            if (isFieldValid == false)
                isValid = isFieldValid;
        });
        return isValid;
    }

    validateRecaptcha() {
        const self = this;

        self.recaptcha.hideErrorMessage();
        const token = document.querySelector("[name=cf-turnstile-response]")?.value ?? null;

        if (isNullOrUndefined(token)) {
            turnstile.reset("#d365turnstile");
            return;
        }

        const validateUrl = `/Recaptcha/ValidateCloudflareRecaptcha?token=${token}`;

        fetch(validateUrl)
            .then(function (response) {
                return response.json();
            })
            .then(function (data) {
                if (data == true) {
                    self.embeddedFormBlock.submitForm();
                } else {
                    turnstile.reset("#d365turnstile");
                    self.recaptcha.showErrorMessage();
                }
            })
            .catch(function (err) {
                console.error("Something went wrong!", err);
                self.recaptcha.showErrorMessage();
            });
    }

    onSubmit() {
        // Update the validation UI state for all inputs that can be validated
        // with the Constraint Validation API.
        const inputElements = this.embeddedFormBlock.form.elements;
        for (const inputEl of inputElements) {
            const isMultiSelect = inputEl.closest("div.multiselect") != null;

            if (!isMultiSelect) {
                this.updateValidationStateForInput(inputEl);
            }
        }

        // The `isFormValid` boolean respresents all inputs that can be
        // validated via the Constraint Validation API.
        const isFormValid = this.embeddedFormBlock.form.checkValidity();

        // Validate phone number field manually to enable checking country phone format
        const isPhoneNumberValid = this.validateAllPhoneNumberFields();
        const isMultiSelectValid = this.validateMultiSelectCheckboxGroups();

        // If any of the validation checks fail, prevent the form from submitting.
        if (!isFormValid || !isPhoneNumberValid || !isMultiSelectValid) {
            // Set the focus to the first invalid input.
            const firstInvalidInputEl = this.embeddedFormBlock.form.querySelector(
                "input:invalid, fieldset.is-invalid input"
            );
            firstInvalidInputEl?.focus();
        } else {
            if (window.turnstile != undefined) {
                this.validateRecaptcha();
            } else {
                this.embeddedFormBlock.submitForm();
            }
        }
    }
}
